import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API, axios } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { BottomNav } from "@/components/shared/Navigation";
import { BrandLogo } from "@/components/shared/BrandLogo";
import { ShareSheet, getShareCopy } from "@/components/shared/ShareSheet";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bookmark, ArrowLeft, Star, Trash2, Gift, Loader2, Sparkles, Share2,
} from "lucide-react";
import { toast } from "sonner";
import TooltipOverlay from "@/components/shared/TooltipOverlay";
import { useTheme } from "@/contexts/ThemeContext";

export default function WishlistPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { mode } = useTheme();
  const [wishlist, setWishlist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [sharing, setSharing] = useState(false);

  const fetchWishlist = async () => {
    try {
      const res = await axios.get(`${API}/wishlist`);
      setWishlist(res.data);
    } catch { toast.error("Couldn't load wishlist"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchWishlist(); }, []);

  const removeItem = async (businessId) => {
    if (!window.confirm("Remove from wishlist?")) return;
    setRemoving(businessId);
    try {
      await axios.post(`${API}/wishlist/${businessId}`);
      setWishlist(prev => ({
        ...prev,
        items: prev.items.filter(i => i.business_id !== businessId),
        count: prev.count - 1,
        total_value: prev.total_value - (prev.items.find(i => i.business_id === businessId)?.offer_value_est || 0),
      }));
      toast.success("Removed from wishlist");
    } catch { toast.error("Couldn't remove"); }
    finally { setRemoving(null); }
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const res = await axios.post(`${API}/wishlist/share`);
      const link = `${window.location.origin}/shared-wishlist/${res.data.share_id}`;
      setShareUrl(link);
      setShareOpen(true);
    } catch {
      toast.error("Couldn't create share link");
    }
    finally { setSharing(false); }
  };

  const windowLabel = (type) => {
    if (!type) return "";
    const map = { birthday_only: "Birthday only", birthday_week: "Birthday week", birthday_month: "Birthday month", any_time: "Any time" };
    return map[type] || type.replace(/_/g, " ");
  };

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }} data-testid="wishlist-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-indigo-500 to-violet-600 text-white px-4 pt-6 pb-5">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-xl bg-white/10 hover:bg-white/20 transition" data-testid="wishlist-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-lg font-bold flex-1">My Wishlist</h1>
          {wishlist && wishlist.count > 0 && (
            <button
              onClick={handleShare}
              disabled={sharing}
              className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition disabled:opacity-50"
              data-testid="wishlist-share-btn"
            >
              {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            </button>
          )}
        </div>
        <p className="text-white/70 text-xs leading-relaxed mb-3">Your birthday deal registry. Share this list with friends and family so they know exactly which deals you want.</p>

        {/* Value banner */}
        {wishlist && wishlist.count > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-3 flex items-center gap-3"
            data-testid="wishlist-value-banner"
          >
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Gift className="w-5 h-5" />
            </div>
            <div>
              <p className="text-white/70 text-xs font-medium">Your birthday wishlist</p>
              <p className="text-xl font-bold">
                ${wishlist.total_value.toFixed(0)} <span className="text-sm font-medium text-white/70">in free stuff saved</span>
              </p>
            </div>
            <Sparkles className="w-5 h-5 text-amber-300 ml-auto" />
          </motion.div>
        )}
      </div>

      {/* Content */}
      <div className="px-4 mt-4 space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
          </div>
        ) : !wishlist || wishlist.count === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
            <div className="w-16 h-16 mx-auto bg-violet-100 rounded-2xl flex items-center justify-center mb-4">
              <Bookmark className="w-7 h-7 text-violet-400" />
            </div>
            <p className="text-slate-700 font-semibold text-lg">Your wishlist is empty</p>
            <p className="text-slate-400 text-sm mt-1 px-6">Add deals you want friends & family to know about. They can view your list and help you claim them.</p>
            <button
              onClick={() => navigate("/my-deals")}
              className="mt-5 px-5 py-2.5 bg-violet-500 text-white text-sm font-semibold rounded-xl hover:bg-violet-600 transition"
              data-testid="wishlist-browse-btn"
            >
              Browse Deals
            </button>
          </motion.div>
        ) : (
          <AnimatePresence>
            {wishlist.items.map((item, i) => (
              <motion.div
                key={item.business_id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ delay: i * 0.04 }}
                onClick={() => navigate(`/offer/${item.business_id}`)}
                className="bg-white rounded-xl border border-slate-200/80 p-3.5 flex items-center gap-3 cursor-pointer hover:shadow-md transition-shadow"
                data-testid={`wishlist-item-${item.business_id}`}
              >
                <BrandLogo name={item.name || item.business_name} logoUrl={item.logo_url} />
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-slate-800 truncate">{item.name || item.business_name}</h3>
                  <p className="text-xs text-slate-500 truncate mt-0.5">{item.offer_title}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    {item.offer_value_est > 0 && (
                      <span className="text-xs font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">${item.offer_value_est}</span>
                    )}
                    {item.birthday_window_type && (
                      <span className="text-[10px] text-slate-400">{windowLabel(item.birthday_window_type)}</span>
                    )}
                    {item.ease_stars > 0 && (
                      <span className="flex items-center gap-0.5 text-[10px] text-slate-400">
                        <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />{item.ease_stars}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); removeItem(item.business_id); }}
                  disabled={removing === item.business_id}
                  className="p-2 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                  data-testid={`wishlist-remove-${item.business_id}`}
                >
                  {removing === item.business_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>
      <ShareSheet
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        url={shareUrl}
        title={getShareCopy(mode, "wishlist", { totalValue: wishlist?.total_value?.toFixed(0) || 0 }).title}
        description={getShareCopy(mode, "wishlist", { totalValue: wishlist?.total_value?.toFixed(0) || 0 }).description}
        businessName="My Wishlist"
        shareContext="wishlist"
      />
      <BottomNav />
      <TooltipOverlay screen="wishlist" targetSelector='[data-testid="wishlist-page"]' message="Your saved deals! Tap any to see details, or share your wishlist with friends." placement="top" />
    </div>
  );
}
